import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { NamedClubAvatar } from '@sorare/core/src/components/club/NamedClubAvatar';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { gameStatusMessages, glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { getTeamName, isGameFinished, isGameLive } from 'lib/so5';

import {
  GameTeamsMatchup_anyGameInterface,
  GameTeamsMatchup_pollingAnyGameInterface,
} from './__generated__/index.graphql';

const Root = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content minmax(0, 1fr);
  align-items: center;
  justify-items: center;
  gap: var(--unit);
  padding: 0 var(--double-unit) var(--double-unit);
  width: 100%;
  overflow: hidden;

  &.reversed {
    > *:first-child {
      order: 2;
    }
    > *:nth-child(3) {
      order: -1;
    }
  }
`;
const Scores = styled(Horizontal)`
  .reversed & {
    flex-direction: row-reverse;
  }
`;
const LiveWrapper = styled(Horizontal)`
  border-radius: var(--half-unit);
  padding: var(--half-unit);
  background-color: rgba(var(--c-rgb-red-600), 0.16);
  color: var(--c-red-800);
`;

type Props = {
  game: GameTeamsMatchup_anyGameInterface;
  displayDay?: boolean;
};
export const GameTeamsMatchup = ({ game, displayDay }: Props) => {
  const homeTeam = {
    name: getTeamName(game.homeTeam),
    avatar: <TeamAvatar team={game.homeTeam} />,
  };
  const awayTeam = {
    name: getTeamName(game.awayTeam),
    avatar: <TeamAvatar team={game?.awayTeam} />,
  };

  const reverseTeamOrder =
    isType(game, 'GameOfBaseball') || isType(game, 'GameOfBasketball');

  const live = isGameLive(game.status);
  const finished = isGameFinished(game.status);

  return (
    <Root className={classNames({ reversed: reverseTeamOrder })}>
      <LabelS as="div">
        <NamedClubAvatar team={homeTeam} />
      </LabelS>
      <Vertical center gap={0.5}>
        <HeadlineM as="span">
          {live || finished ? (
            <Scores gap={0.5}>
              <span>{game.homeScore}</span>
              <span>-</span>
              <span>{game.awayScore}</span>
            </Scores>
          ) : (
            <FormattedDate value={game.date} hour="numeric" minute="numeric" />
          )}
          {live && (
            <LiveWrapper center>
              <Horizontal gap={0.5}>
                <LiveDot animate size="sm" />
                <LabelS as="span" bold>
                  {isType(game, 'Game') ? (
                    <>{game.minute}&apos;</>
                  ) : (
                    <>
                      {isType(game, 'GameOfBasketball') ? (
                        <>
                          {game.isHalftime ? (
                            <FormattedMessage
                              id="Game.status.halftime"
                              defaultMessage="Half"
                            />
                          ) : (
                            <FormattedMessage
                              {...glossary.ordinal}
                              values={{ ordinal: game.quarter }}
                            />
                          )}
                        </>
                      ) : (
                        <FormattedMessage {...gameStatusMessages.live} />
                      )}
                    </>
                  )}
                </LabelS>
              </Horizontal>
            </LiveWrapper>
          )}
        </HeadlineM>
        {displayDay && (
          <Chip>
            <FormattedDate value={game.date} month="short" day="2-digit" />
          </Chip>
        )}
      </Vertical>
      <LabelS as="div">
        <NamedClubAvatar team={awayTeam} />
      </LabelS>
    </Root>
  );
};

GameTeamsMatchup.pollingFragments = {
  anyGameInterface: gql`
    fragment GameTeamsMatchup_pollingAnyGameInterface on AnyGameInterface {
      id
      status: statusTyped
      date
      ... on Game {
        id
        minute
      }
      ... on GameOfBasketball {
        id
        isHalftime
        quarter
      }
      homeScore
      awayScore
    }
  ` as TypedDocumentNode<GameTeamsMatchup_pollingAnyGameInterface>,
};

GameTeamsMatchup.fragments = {
  anyGameInterface: gql`
    fragment GameTeamsMatchup_anyGameInterface on AnyGameInterface {
      id
      status: statusTyped
      date
      ... on Game {
        id
        minute
      }
      ... on GameOfBasketball {
        id
        isHalftime
        quarter
      }
      homeScore
      awayScore
      homeTeam {
        slug
        ...TeamAvatar_team
        ...getTeamName_team
      }
      awayTeam {
        slug
        ...TeamAvatar_team
        ...getTeamName_team
      }
      ...GameTeamsMatchup_pollingAnyGameInterface
    }
    ${GameTeamsMatchup.pollingFragments.anyGameInterface}
    ${TeamAvatar.fragments.team}
    ${getTeamName.fragments.team}
  ` as TypedDocumentNode<GameTeamsMatchup_anyGameInterface>,
};
